import React from 'react';
import { createContext, useContext, useState, useEffect} from 'react';
import type {ReactNode } from 'react';
import { PublicClientApplication } from '@azure/msal-browser';

interface User {
  _id: string;
  id?: string;
  email: string;
  firstname: string;
  lastname: string;
  isActive: boolean;
  role?: string;
  phone?: string;
  enterprise?: string;
  mail2FA?: boolean;
}

interface License {
  _id: string;
  plan: {
    name: 'UNACTIVE' | 'FREE' | 'PRO' | 'ENTERPRISE';
    displayName: string;
  };
  isActive: boolean;
  issuedAt: string;
  expiresAt?: string;
  paymentStatus: 'incomplete' | 'incomplete_expired' | 'trialing' | 'active' | 'past_due' | 'canceled' | 'unpaid';
  createdByStripe: boolean;
  owner: {
    _id: string;
    firstname: string;
    lastname: string;
    email: string;
    phone: string;
  };
  __v: number;
}

interface LicenseKey {
  key: string;
  plan: string;
}

interface LicenseResponse {
  licenses: License[];
  total: number;
  page: number;
  limit: number;
  totalPages: number;
}

interface AuthContextType {
  user: User | null;
  token: string | null;
  isAuthenticated: boolean;
  login: (email: string, password: string) => Promise<{ requiresTwoFA?: boolean; accountId?: string }>;
  googleLogin: (accessToken: string) => Promise<User>;
  azureLogin: (accessToken: string) => Promise<User>;
  register: (userData: RegisterData) => Promise<void>;
  verifyEmail: (email: string, code: string) => Promise<void>;
  verify2FA: (accountId: string, code: string) => Promise<void>;
  resendVerificationCode: (email: string) => Promise<void>;
  getUserProfile: () => Promise<User>;
  updateUserProfile: (userData: Partial<User>) => Promise<User>;
  deleteUserAccount: () => Promise<void>;
  getUserLicense: () => Promise<License>;
  getUserLicenseKey: () => Promise<LicenseKey>;
  createCheckoutSession: (plan: 'pro' | 'enterprise') => Promise<string>;
  createTrialSession: (plan: 'pro') => Promise<string>;
  cancelSubscription: () => Promise<void>;
  logout: () => void;
  loading: boolean;
}

interface RegisterData {
  firstname: string;
  lastname: string;
  email: string;
  password: string;
  phone?: string;
  enterprise?: string;
  rgpdConsent: boolean;
  newsletterConsent: boolean;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

interface AuthProviderProps {
  children: ReactNode;
  msalInstance?: PublicClientApplication;
}

export const AuthProvider: React.FC<AuthProviderProps> = ({ children, msalInstance }) => {
  const [user, setUser] = useState<User | null>(null);
  const [token, setToken] = useState<string | null>(null);
  const [refreshToken, setRefreshToken] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const API_BASE_URL = import.meta.env.VITE_API_BASE_URL || 'http://localhost:3001/api';

  const refreshAccessToken = async (): Promise<string | null> => {
    const currentRefreshToken = refreshToken || localStorage.getItem('refreshToken');
    
    if (!currentRefreshToken) {
      return null;
    }

    try {
      const response = await fetch(`${API_BASE_URL}/accounts/refresh-token`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ refreshToken: currentRefreshToken }),
      });

      if (response.ok) {
        const { token } = await response.json();
        setToken(token);
        localStorage.setItem('accessToken', token);
        return token;
      } else {
        await logout();
        return null;
      }
    } catch (error) {
      await logout();
      return null;
    }
  };

  const authenticatedFetch = async (url: string, options: RequestInit = {}): Promise<Response> => {
    let currentToken = token || localStorage.getItem('accessToken');
    
    const headers = {
      ...options.headers,
      'Authorization': `Bearer ${currentToken}`,
    };

    let response = await fetch(url, { ...options, headers });

    if (response.status === 401 || response.status === 403) {
      const newToken = await refreshAccessToken();
      
      if (newToken) {
        const newHeaders = {
          ...options.headers,
          'Authorization': `Bearer ${newToken}`,
        };
        response = await fetch(url, { ...options, headers: newHeaders });
      }
    }

    return response;
  };

  useEffect(() => {
    const storedToken = localStorage.getItem('accessToken');
    const storedRefreshToken = localStorage.getItem('refreshToken');
    const storedUser = localStorage.getItem('auth_user');
    
    if (storedToken && storedUser) {
      try {
        setToken(storedToken);
        setRefreshToken(storedRefreshToken);
        setUser(JSON.parse(storedUser));
      } catch (error) {
        localStorage.removeItem('accessToken');
        localStorage.removeItem('refreshToken');
        localStorage.removeItem('auth_user');
      }
    }
    setLoading(false);
  }, []);

  const login = async (email: string, password: string): Promise<{ requiresTwoFA?: boolean; accountId?: string }> => {
    try {
      const response = await fetch(`${API_BASE_URL}/accounts/login`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ email, password }),
      });
      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || 'Login failed');
      }
      const data = await response.json();
      if (data.requiresTwoFA) {
        return { requiresTwoFA: true, accountId: data.accountId };
      }
      
      setToken(data.token);
      setUser(data.account);
      if (data.refreshToken) {
        setRefreshToken(data.refreshToken);
        localStorage.setItem('refreshToken', data.refreshToken);
      }
      
      localStorage.setItem('accessToken', data.token);
      localStorage.setItem('auth_user', JSON.stringify(data.account));
      
      return {};
    } catch (error) {
      throw error;
    }
  };

  const googleLogin = async (accessToken: string): Promise<User> => {
    try {
      const response = await fetch(`${API_BASE_URL}/accounts/auth/google`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ access_token: accessToken }),
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || 'Google authentication failed');
      }

      const data = await response.json();
      
      setToken(data.token);
      setUser(data.account);
      if (data.refreshToken) {
        setRefreshToken(data.refreshToken);
        localStorage.setItem('refreshToken', data.refreshToken);
      }
      
      localStorage.setItem('accessToken', data.token);
      localStorage.setItem('auth_user', JSON.stringify(data.account));
      
      return data.account;
    } catch (error) {
      throw error;
    }
  };

  const azureLogin = async (accessToken: string): Promise<User> => {
    try {
      const response = await fetch(`${API_BASE_URL}/accounts/auth/azure`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ access_token: accessToken }),
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || 'Azure authentication failed');
      }

      const data = await response.json();
      
      setToken(data.token);
      setUser(data.account);
      if (data.refreshToken) {
        setRefreshToken(data.refreshToken);
        localStorage.setItem('refreshToken', data.refreshToken);
      }
      
      localStorage.setItem('accessToken', data.token);
      localStorage.setItem('auth_user', JSON.stringify(data.account));
      
      return data.account;
    } catch (error) {
      throw error;
    }
  };

  const register = async (userData: RegisterData): Promise<void> => {
    try {
      const response = await fetch(`${API_BASE_URL}/accounts/register`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(userData),
      });
      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || 'Registration failed');
      }
      const data = await response.json();
    } catch (error) {
      throw error;
    }
  };

  const verifyEmail = async (email: string, code: string): Promise<void> => {
    try {
      const response = await fetch(`${API_BASE_URL}/verification/verify`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ email, code }),
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || 'Verification failed');
      }

      const data = await response.json();
    } catch (error) {
      throw error;
    }
  };

  const verify2FA = async (accountId: string, code: string): Promise<void> => {
    try {
      const response = await fetch(`${API_BASE_URL}/accounts/verify-2fa`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ accountId, code }),
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || '2FA verification failed');
      }

      const data = await response.json();
      
      setToken(data.token);
      setUser(data.account);
      if (data.refreshToken) {
        setRefreshToken(data.refreshToken);
        localStorage.setItem('refreshToken', data.refreshToken);
      }
      
      localStorage.setItem('accessToken', data.token);
      localStorage.setItem('auth_user', JSON.stringify(data.account));
    } catch (error) {
      throw error;
    }
  };

  const resendVerificationCode = async (email: string): Promise<void> => {
    try {
      const response = await fetch(`${API_BASE_URL}/verification/send`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ email }),
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || 'Failed to resend code');
      }

      const data = await response.json();
    } catch (error) {
      throw error;
    }
  };

  const getUserProfile = async (): Promise<User> => {
    if (!token) {
      throw new Error('Not authenticated');
    }

    try {
      const response = await authenticatedFetch(`${API_BASE_URL}/accounts/profile/me`, {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
        },
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || 'Failed to get profile');
      }

      const data = await response.json();
      setUser(data);
      localStorage.setItem('auth_user', JSON.stringify(data));
      return data;
    } catch (error) {
      throw error;
    }
  };

  const updateUserProfile = async (userData: Partial<User>): Promise<User> => {
    if (!token || !user) {
      throw new Error('Not authenticated');
    }

    try {
      const response = await authenticatedFetch(`${API_BASE_URL}/accounts/me`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(userData),
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || 'Failed to update profile');
      }

      const data = await response.json();
      const updatedUser = data.account;
      setUser(updatedUser);
      localStorage.setItem('auth_user', JSON.stringify(updatedUser));
      return updatedUser;
    } catch (error) {
      throw error;
    }
  };

  const deleteUserAccount = async (): Promise<void> => {
    if (!token || !user) {
      throw new Error('Not authenticated');
    }

    try {
      const response = await authenticatedFetch(`${API_BASE_URL}/accounts/me`, {
        method: 'DELETE',
        headers: {
          'Content-Type': 'application/json',
        },
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || 'Failed to delete account');
      }

      await logout();
    } catch (error) {
      throw error;
    }
  };

  const getUserLicense = async (): Promise<License> => {
    if (!token) {
      throw new Error('Not authenticated');
    }

    try {
      const response = await authenticatedFetch(`${API_BASE_URL}/licenses/me`, {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
        },
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || 'Failed to get license');
      }
      const data: LicenseResponse = await response.json();
      if (data.licenses && data.licenses.length > 0) {
        return data.licenses[0];
      }
      
      throw new Error('No license found');
    } catch (error) {
      throw error;
    }
  };

  const createCheckoutSession = async (plan: 'pro' | 'enterprise'): Promise<string> => {
    if (!token) {
      throw new Error('Not authenticated');
    }

    try {
      const response = await authenticatedFetch(`${API_BASE_URL}/payments/create-checkout-session/${plan}`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
      });
      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || 'Failed to create checkout session');
      }
      const data = await response.json();
      return data.url;
    } catch (error) {
      throw error;
    }
  };

  const createTrialSession = async (plan: 'pro'): Promise<string> => {
    if (!token) {
      throw new Error('Not authenticated');
    }

    try {
      const response = await authenticatedFetch(`${API_BASE_URL}/payments/create-trial-session/${plan}`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
      });
      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || 'Failed to create trial session');
      }
      const data = await response.json();
      return data.url;
    } catch (error) {
      throw error;
    }
  };

  const cancelSubscription = async (): Promise<void> => {
    if (!token) {
      throw new Error('Not authenticated');
    }

    try {
      const response = await authenticatedFetch(`${API_BASE_URL}/payments/cancel`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
      });
      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || 'Failed to cancel subscription');
      }
      const data = await response.json();
    } catch (error) {
      throw error;
    }
  };

  const getUserLicenseKey = async (): Promise<LicenseKey> => {
    if (!token) {
      throw new Error('Not authenticated');
    }

    try {
      const response = await authenticatedFetch(`${API_BASE_URL}/licenses/key/me`, {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
        },
      });
      if (!response.ok) {
        const responseText = await response.text();
        if (response.status === 404) {
          throw new Error('No active license found');
        }
        try {
          const errorData = JSON.parse(responseText);
          throw new Error(errorData.error || 'Failed to get license key');
        } catch (parseError) {
          throw new Error(`API Error (${response.status}): ${responseText.substring(0, 200)}...`);
        }
      }
      const responseText = await response.text();      
      const data: LicenseKey = JSON.parse(responseText);
      return data;
    } catch (error) {
      throw error;
    }
  };

  const clearGoogleAuth = () => {
    const iframe = document.createElement('iframe');
    iframe.style.display = 'none';
    iframe.src = 'https://accounts.google.com/logout';
    document.body.appendChild(iframe);
    
    setTimeout(() => {
      document.body.removeChild(iframe);
    }, 1000);
  };

  const clearAzureAuth = async () => {
    if (msalInstance) {
      try {
        const accounts = msalInstance.getAllAccounts();
        if (accounts.length > 0) {
          accounts.forEach(account => {
            msalInstance.removeAccount(account);
          });
        }
        msalInstance.clearCache();
        
        try {
          sessionStorage.clear();
        } catch (storageError) {
          console.error('Error clearing session storage');
        }
      } catch (error) {
        console.error('Error clearing Azure auth');
      }
    }
  };

  const logout = async () => {
    const currentRefreshToken = refreshToken || localStorage.getItem('refreshToken');
    const currentAccessToken = token || localStorage.getItem('accessToken');

    if (currentRefreshToken && currentAccessToken) {
      try {
        await fetch(`${API_BASE_URL}/accounts/revoke-token`, {
          method: 'POST',
          headers: {
            'Authorization': `Bearer ${currentAccessToken}`,
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({ refreshToken: currentRefreshToken }),
        });
      } catch (error) {
        console.error('Error revoking token');
      }
    }

    setUser(null);
    setToken(null);
    setRefreshToken(null);
    
    localStorage.removeItem('accessToken');
    localStorage.removeItem('refreshToken');
    localStorage.removeItem('auth_user');
    
    const authKeys = ['accessToken', 'refreshToken', 'auth_user'];
    authKeys.forEach(key => {
      localStorage.removeItem(key);
      sessionStorage.removeItem(key);
    });
    
    clearGoogleAuth();
    await clearAzureAuth();
  };

  const value: AuthContextType = {
    user,
    token,
    isAuthenticated: !!user && !!token,
    login,
    googleLogin,
    azureLogin,
    register,
    verifyEmail,
    verify2FA,
    resendVerificationCode,
    getUserProfile,
    updateUserProfile,
    deleteUserAccount,
    getUserLicense,
    getUserLicenseKey,
    createCheckoutSession,
    createTrialSession,
    cancelSubscription,
    logout,
    loading,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};